import * as React from "react"
import styled, { useTheme } from "styled-components"
import carImage from "../../../images/Car.svg"
import carFrontImage from "../../../images/CarFront.svg"
import { theme } from "../../theme"

const RADIUS = 100
const LABEL_PADDING = 30
const SVG_SIZE = (RADIUS + LABEL_PADDING) * 2
const CENTER = { x: SVG_SIZE / 2, y: SVG_SIZE / 2 }
const RING_WIDTH = 34
const SECTOR_RADIUS = RADIUS + RING_WIDTH / 2
const LABEL_RADIUS = SECTOR_RADIUS + 10
const MAX_LABEL_CHARS = 14
const LABEL_FONT_SIZE = 13
// Rough average glyph width for Inter, used only until the text has been measured for real.
const ESTIMATED_CHAR_WIDTH_RATIO = 0.55
const ANGLE_SMOOTHING = 0.2
const ANGLE_SETTLE_THRESHOLD = 0.05

export function polarToCartesian(centerX: number, centerY: number, radius: number, angleDeg: number) {
  const angleRad = (angleDeg - 90) * (Math.PI / 180)
  return {
    x: centerX + radius * Math.cos(angleRad),
    y: centerY + radius * Math.sin(angleRad),
  }
}

export function sectorPoint(radius: number, angleDeg: number) {
  return polarToCartesian(CENTER.x, CENTER.y, radius, angleDeg)
}

const sectorStart = sectorPoint(SECTOR_RADIUS, -8)
const sectorEnd = sectorPoint(SECTOR_RADIUS, 8)
const sectorPath = `M ${CENTER.x} ${CENTER.y} L ${sectorStart.x} ${sectorStart.y} A ${SECTOR_RADIUS} ${SECTOR_RADIUS} 0 0 1 ${sectorEnd.x} ${sectorEnd.y} Z`

export function lerpColor(colorA: string, colorB: string, t: number) {
  const a = parseInt(colorA.slice(1), 16)
  const b = parseInt(colorB.slice(1), 16)
  const ar = (a >> 16) & 0xff
  const ag = (a >> 8) & 0xff
  const ab = a & 0xff
  const br = (b >> 16) & 0xff
  const bg = (b >> 8) & 0xff
  const bb = b & 0xff
  const r = Math.round(ar + (br - ar) * t)
  const g = Math.round(ag + (bg - ag) * t)
  const bl = Math.round(ab + (bb - ab) * t)
  return `#${((1 << 24) + (r << 16) + (g << 8) + bl).toString(16).slice(1)}`
}

export function signalStrengthToColor(
  signalStrength: number,
  colors: { blue: string; blueBorder: string } = { blue: theme.colors.blue, blueBorder: theme.gradients.blueBorder },
) {
  const clamped = Math.min(1, Math.max(0, signalStrength))
  return lerpColor(colors.blue, colors.blueBorder, (clamped - 0.5) / 0.5)
}

export function truncateLabel(label: string, maxChars: number = MAX_LABEL_CHARS) {
  return label.length > maxChars ? `${label.slice(0, maxChars - 1).trimEnd()}\u2026` : label
}

// How far past the clearance radius the centre of a label has to sit to clear that radius at the
// given angle. The label is treated as an ellipse through the corners of its half width and half
// height rather than as its exact box: the box's own support function has a corner wherever the
// radial direction turns vertical, which the eye reads as a bounce as the label crosses the top of
// the ring. This is smooth at every angle, and matches the box exactly at the four cardinal points.
export function radialLabelOffset(width: number, height: number, angleDeg: number) {
  const angleRad = (angleDeg - 90) * (Math.PI / 180)
  return Math.hypot(Math.cos(angleRad) * (width / 2), Math.sin(angleRad) * (height / 2))
}

function estimateTextExtent(label: string) {
  return { width: label.length * LABEL_FONT_SIZE * ESTIMATED_CHAR_WIDTH_RATIO, height: LABEL_FONT_SIZE }
}

function useTextExtent(label: string) {
  const textRef = React.useRef<SVGTextElement>(null)
  const [extent, setExtent] = React.useState(() => estimateTextExtent(label))

  React.useLayoutEffect(() => {
    const node = textRef.current
    // getBBox is unavailable in jsdom and returns zeroes for a text node that has not been laid
    // out yet (a hidden tab, say), so fall back to the estimate rather than collapsing the offset.
    const box = node && typeof node.getBBox === "function" ? node.getBBox() : undefined
    setExtent(box && box.width > 0 ? { width: box.width, height: box.height } : estimateTextExtent(label))
  }, [label])

  return [textRef, extent] as const
}

export function normalizeDegrees(degrees: number) {
  return ((degrees % 360) + 360) % 360
}

export function shortestAngleDifference(fromDegrees: number, toDegrees: number) {
  return normalizeDegrees(toDegrees - fromDegrees + 180) - 180
}

export function angleWithShortestWrap(currentAngleDeg: number, targetAngleDeg: number) {
  return currentAngleDeg + shortestAngleDifference(currentAngleDeg, targetAngleDeg)
}

// The antenna's current azimuth is a compass bearing, while the motor angle is the same pointing
// direction measured from the vehicle's nose, so the difference between them is the vehicle heading.
export function vehicleHeadingFromAntenna(antennaAzimuthDeg: number, motorLocalAzimuthDeg: number) {
  return normalizeDegrees(antennaAzimuthDeg - motorLocalAzimuthDeg)
}

type Orientation = {
  targetAngleDeg: number
  vehicleHeadingDeg: number
}

function useSmoothedAngle(targetDeg: number | undefined) {
  const [angle, setAngle] = React.useState(targetDeg ?? 0)
  const angleRef = React.useRef(angle)
  const hasReceivedValue = React.useRef(targetDeg !== undefined)

  React.useEffect(() => {
    if (targetDeg === undefined) {
      return
    }

    if (!hasReceivedValue.current) {
      hasReceivedValue.current = true
      angleRef.current = targetDeg
      setAngle(targetDeg)
      return
    }

    let animationFrame: number

    const animate = () => {
      const wrappedTargetAngle = angleWithShortestWrap(angleRef.current, targetDeg)
      const difference = wrappedTargetAngle - angleRef.current

      if (Math.abs(difference) <= ANGLE_SETTLE_THRESHOLD) {
        angleRef.current = wrappedTargetAngle
        setAngle(angleRef.current)
        return
      }

      angleRef.current = angleRef.current + difference * ANGLE_SMOOTHING
      setAngle(angleRef.current)
      animationFrame = requestAnimationFrame(animate)
    }

    animationFrame = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animationFrame)
  }, [targetDeg])

  return angle
}

function useSmoothedOrientation(
  targetAngleDeg: number | undefined,
  vehicleHeadingDeg: number | undefined,
): Orientation {
  return {
    targetAngleDeg: useSmoothedAngle(targetAngleDeg),
    vehicleHeadingDeg: useSmoothedAngle(vehicleHeadingDeg),
  }
}

const VisualizationFrame = styled.div`
  position: relative;
  width: ${SVG_SIZE}px;
  height: ${SVG_SIZE}px;
`

// The labels orbit just outside the ring and would otherwise be clipped by the SVG viewport.
// Painting outside it keeps the layout footprint at the frame size, without extra whitespace.
const ChartSvg = styled.svg`
  overflow: visible;
`

// The label sweeps through the bottom-right corner, where callers may overlay something on top of
// the dial (the inset elevation chart, say). Drawing it in its own raised layer keeps it readable
// there without lifting the ring itself above that overlay.
const LabelOverlaySvg = styled(ChartSvg)`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  pointer-events: none;
`

export const MotorOrientationChart: React.FunctionComponent<{
  targetLabel: string
  targetAngleDeg: number | undefined
  vehicleHeadingDeg: number | undefined
  coneRotationDeg: number | undefined
  coneColor: string
  otherSatelliteAngles: number[]
}> = ({ targetLabel, targetAngleDeg, vehicleHeadingDeg, coneRotationDeg, coneColor, otherSatelliteAngles }) => {
  const activeTheme = useTheme()
  const smoothedOrientation = useSmoothedOrientation(targetAngleDeg, vehicleHeadingDeg)
  const smoothedConeRotation = useSmoothedAngle(coneRotationDeg)
  const satellitePos = polarToCartesian(CENTER.x, CENTER.y, RADIUS, smoothedOrientation.targetAngleDeg)
  // The dial is vehicle-relative (the car stays fixed pointing "up"), so true north — an absolute
  // compass bearing — appears at the negated vehicle heading, not at the heading itself.
  const northPos = polarToCartesian(CENTER.x, CENTER.y, RADIUS, -smoothedOrientation.vehicleHeadingDeg)
  const truncatedTargetLabel = truncateLabel(targetLabel)
  const [labelRef, labelExtent] = useTextExtent(truncatedTargetLabel)
  const labelPos = polarToCartesian(
    CENTER.x,
    CENTER.y,
    LABEL_RADIUS + radialLabelOffset(labelExtent.width, labelExtent.height, smoothedOrientation.targetAngleDeg),
    smoothedOrientation.targetAngleDeg,
  )

  return (
    <VisualizationFrame>
      <ChartSvg width={SVG_SIZE} height={SVG_SIZE}>
        <circle
          cx={CENTER.x}
          cy={CENTER.y}
          r={RADIUS}
          fill="none"
          stroke={activeTheme.colors.graphRing}
          strokeWidth={RING_WIDTH}
        />
        <path
          d={sectorPath}
          fill={coneColor}
          shapeRendering="geometricPrecision"
          style={{
            transform: `rotate(${smoothedConeRotation}deg)`,
            transformOrigin: `${CENTER.x}px ${CENTER.y}px`,
          }}
        />
        <image href={carImage} x={CENTER.x - 20} y={CENTER.y - 47} width={40} height={94} />
        <text
          x={northPos.x}
          y={northPos.y}
          fill={activeTheme.colors.greyDarker}
          fontFamily="Inter, sans-serif"
          fontSize={13}
          fontWeight={600}
          textAnchor="middle"
          dominantBaseline="middle"
        >
          N
        </text>
        {otherSatelliteAngles.map((angleDeg) => {
          const pos = polarToCartesian(CENTER.x, CENTER.y, RADIUS, angleDeg + smoothedOrientation.targetAngleDeg)
          return <circle key={angleDeg} cx={pos.x} cy={pos.y} r={6} fill={activeTheme.colors.graphColorBlue} />
        })}
        <circle
          cx={satellitePos.x}
          cy={satellitePos.y}
          r={6}
          fill={activeTheme.gradients.blueBorder}
          stroke={activeTheme.colors.white}
          strokeWidth={2}
        />
      </ChartSvg>
      <LabelOverlaySvg width={SVG_SIZE} height={SVG_SIZE}>
        <text
          ref={labelRef}
          x={labelPos.x}
          y={labelPos.y}
          fill={activeTheme.colors.greyDarker}
          fontFamily="Inter, sans-serif"
          fontSize={LABEL_FONT_SIZE}
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {truncatedTargetLabel}
        </text>
      </LabelOverlaySvg>
    </VisualizationFrame>
  )
}

const ELEVATION_BELOW = 20
const ELEVATION_RADIUS = 100
const COMPACT_ELEVATION_RADIUS = 80
const CAR_FRONT_WIDTH = 54
const CAR_FRONT_HEIGHT = 36

const ElevationFrame = styled.div<{ $width: number; $height: number }>`
  position: relative;
  width: ${(props) => props.$width}px;
  height: ${(props) => props.$height}px;
`

// 0deg elevation points at the right horizon, 90deg points straight up (zenith).
export const MotorElevationChart: React.FunctionComponent<{
  label?: string
  elevationDeg: number | undefined
  coneColor: string
  compact?: boolean
}> = ({ label, elevationDeg, coneColor, compact = false }) => {
  const activeTheme = useTheme()
  const elevationSize = React.useMemo(() => {
    const radius = compact ? COMPACT_ELEVATION_RADIUS : ELEVATION_RADIUS
    const labelRadius = radius + RING_WIDTH / 2 + 10
    const svgSize = (radius + LABEL_PADDING) * 2
    const center = { x: svgSize / 2, y: radius + LABEL_PADDING }
    const sectorRadius = radius + RING_WIDTH / 2
    const groundLeft = polarToCartesian(center.x, center.y, radius, -90)
    const groundRight = polarToCartesian(center.x, center.y, radius, 90)
    const sectorStart = polarToCartesian(center.x, center.y, sectorRadius, -8)
    const sectorEnd = polarToCartesian(center.x, center.y, sectorRadius, 8)

    return {
      radius,
      center,
      labelRadius,
      svgSize,
      svgHeight: center.y + ELEVATION_BELOW,
      ringPath: `M ${groundLeft.x} ${groundLeft.y} A ${radius} ${radius} 0 0 1 ${groundRight.x} ${groundRight.y}`,
      sectorPath: `M ${center.x} ${center.y} L ${sectorStart.x} ${sectorStart.y} A ${sectorRadius} ${sectorRadius} 0 0 1 ${sectorEnd.x} ${sectorEnd.y} Z`,
    }
  }, [compact])
  const smoothedElevationDeg = useSmoothedAngle(elevationDeg)
  const pointerAngleDeg = 90 - smoothedElevationDeg
  const tipPos = polarToCartesian(elevationSize.center.x, elevationSize.center.y, elevationSize.radius, pointerAngleDeg)
  const truncatedLabel = truncateLabel(label ?? "")
  const [labelRef, labelExtent] = useTextExtent(truncatedLabel)
  const labelPos = polarToCartesian(
    elevationSize.center.x,
    elevationSize.center.y,
    elevationSize.labelRadius + radialLabelOffset(labelExtent.width, labelExtent.height, pointerAngleDeg),
    pointerAngleDeg,
  )

  return (
    <ElevationFrame $width={elevationSize.svgSize} $height={elevationSize.svgHeight}>
      <ChartSvg width={elevationSize.svgSize} height={elevationSize.svgHeight}>
        <path d={elevationSize.ringPath} fill="none" stroke={activeTheme.colors.graphRing} strokeWidth={RING_WIDTH} />
        <path
          d={elevationSize.sectorPath}
          fill={coneColor}
          shapeRendering="geometricPrecision"
          style={{
            transform: `rotate(${pointerAngleDeg}deg)`,
            transformOrigin: `${elevationSize.center.x}px ${elevationSize.center.y}px`,
          }}
        />
        <image
          href={carFrontImage}
          x={elevationSize.center.x - CAR_FRONT_WIDTH / 2}
          y={elevationSize.center.y - CAR_FRONT_HEIGHT / 2}
          width={CAR_FRONT_WIDTH}
          height={CAR_FRONT_HEIGHT}
        />
        <circle
          cx={tipPos.x}
          cy={tipPos.y}
          r={6}
          fill={activeTheme.colors.graphColorBlue}
          stroke={activeTheme.colors.white}
          strokeWidth={2}
        />
        {label && (
          <text
            ref={labelRef}
            x={labelPos.x}
            y={labelPos.y}
            fill={activeTheme.colors.greyDarker}
            fontFamily="Inter, sans-serif"
            fontSize={LABEL_FONT_SIZE}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {truncatedLabel}
          </text>
        )}
      </ChartSvg>
    </ElevationFrame>
  )
}
